import React, { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { wineData } from "../Components/wineData"; 

const ProductDetail = () => {
    // Get the product ID from the URL
    const { id } = useParams();
    
    // State for gallery index
    const [activeIndex, setActiveIndex] = useState(0);

    // State for swipe gestures
    const [touchStart, setTouchStart] = useState(null);
    const [touchEnd, setTouchEnd] = useState(null);
    
    // Find the specific wine data
    const product = wineData.find((wine) => wine.id === id);

    if (!product) {
        return <div className="text-center py-20 text-2xl">Product not found!</div>;
    }

    const isOutOfStock = product.inStock === false;

    // Combine the image and videos into a single array
    const mediaItems = [
        { type: "image", url: product.image },
        ...(product.videos ? product.videos.map(vid => ({ type: "video", url: vid })) : [])
    ];

    // --- NAVIGATION LOGIC ---
    const handleNext = () => {
        setActiveIndex((prevIndex) => (prevIndex + 1) % mediaItems.length);
    };

    const handlePrev = () => {
        setActiveIndex((prevIndex) => (prevIndex - 1 + mediaItems.length) % mediaItems.length);
    };

    // --- SWIPE LOGIC ---
    const minSwipeDistance = 50; // Minimum distance to register a swipe

    const onTouchStart = (e) => {
        setTouchEnd(null); // Reset touch end
        setTouchStart(e.targetTouches[0].clientX);
    };

    const onTouchMove = (e) => {
        setTouchEnd(e.targetTouches[0].clientX);
    };

    const onTouchEnd = () => {
        if (!touchStart || !touchEnd) return;
        const distance = touchStart - touchEnd;
        
        const isLeftSwipe = distance > minSwipeDistance;
        const isRightSwipe = distance < -minSwipeDistance;

        if (isLeftSwipe) {
            handleNext(); // Swiped left -> Go Next
        } else if (isRightSwipe) {
            handlePrev(); // Swiped right -> Go Previous
        }
    };

    return (
        <section className="bg-white px-6 py-12 max-w-6xl mx-auto">
            <Link to="/products" className="text-[--primary-wine] font-semibold hover:underline mb-8 inline-block">
                &larr; Back to all wines
            </Link>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
                
                {/* LEFT COLUMN: Media Gallery & CTA */}
                <div className="flex flex-col items-center p-8 bg-[--neutral-light-gray] rounded-xl shadow-sm">
                    
                    {/* --- MAIN MEDIA VIEWER --- */}
                    <div 
                        className="relative w-full max-w-md h-auto sm:h-auto bg-white rounded-lg shadow-sm border border-gray-100 flex items-center justify-center overflow-hidden mb-4 group"
                        onTouchStart={onTouchStart}
                        onTouchMove={onTouchMove}
                        onTouchEnd={onTouchEnd}
                    >
                        {mediaItems[activeIndex].type === "image" ? (
                            <img
                                src={mediaItems[activeIndex].url}
                                alt={product.name}
                                className={`w-full h-full object-contain p-4 select-none ${isOutOfStock ? "grayscale opacity-70" : ""}`}
                            />
                        ) : (
                            <video 
                                src={mediaItems[activeIndex].url} 
                                controls 
                                autoPlay 
                                className="w-full h-full object-contain bg-black"
                            >
                                Your browser does not support the video tag.
                            </video>
                        )}

                        {/* Next/Prev Buttons (Only show if there's more than 1 item) */}
                        {mediaItems.length > 1 && (
                            <>
                                <button 
                                    onClick={handlePrev}
                                    className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/30 hover:bg-black/60 text-white w-10 h-10 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity md:flex hidden"
                                    aria-label="Previous"
                                >
                                    &#10094;
                                </button>
                                <button 
                                    onClick={handleNext}
                                    className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/30 hover:bg-black/60 text-white w-10 h-10 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity md:flex hidden"
                                    aria-label="Next"
                                >
                                    &#10095;
                                </button>
                            </>
                        )}
                    </div>

                    {/* --- THUMBNAIL ROW --- */}
                    {mediaItems.length > 1 && (
                        <div className="w-full max-w-md flex gap-3 overflow-x-auto pb-2 mb-8 justify-center">
                            {mediaItems.map((media, index) => (
                                <button 
                                    key={index}
                                    onClick={() => setActiveIndex(index)}
                                    className={`relative flex-shrink-0 w-16 h-16 sm:w-20 sm:h-20 rounded-md overflow-hidden border-2 transition-all duration-200 ${
                                        activeIndex === index ? "border-[--primary-wine] shadow-md scale-105" : "border-transparent opacity-70 hover:opacity-100"
                                    }`}
                                >
                                    {media.type === "image" ? (
                                        <img src={media.url} alt="thumbnail" className="w-full h-full object-cover bg-white" />
                                    ) : (
                                        <>
                                            <video src={media.url} className="w-full h-full object-cover bg-black" muted />
                                            <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                                                <span className="text-white text-xl">▶</span>
                                            </div>
                                        </>
                                    )}
                                </button>
                            ))}
                        </div>
                    )}

                    {/* Price and CTA */}
                    <div className="w-full max-w-md mt-auto">
                        <h3 className="text-3xl text-[var(--primary-wine)] font-semibold mb-6 text-center">
                            <span className="text-lg">Rs</span> <span className="text-4xl font-bold">{product.price}</span>
                        </h3>
                        
                        {isOutOfStock ? (
                            <>
                                <div className="w-full text-center px-6 py-4 bg-gray-300 text-gray-600 text-lg font-bold rounded-lg cursor-not-allowed block">
                                    Out of Stock
                                </div>
                                <a
                                    href={`https://wa.me/919881055454?text=${encodeURIComponent(`Hi! Please notify me when ${product.name} is back in stock.`)}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="w-full text-center mt-3 px-6 py-3 border-2 border-[--primary-wine] text-[--primary-wine] font-semibold rounded-lg hover:bg-[--primary-wine] hover:text-white transition block"
                                >
                                    Notify Me on WhatsApp
                                </a>
                            </>
                        ) : (
                            <a
                                href={`https://wa.me/919881055454?text=Hi! I'm interested in buying: ${encodeURIComponent(product.name)} for Rs ${product.price}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="w-full text-center px-6 py-4 bg-[--primary-wine] text-white text-lg font-bold rounded-lg hover:bg-[--primary-wine-hover] transition shadow-md block"
                            >
                                Buy Now on WhatsApp
                            </a>
                        )}
                    </div>
                </div>

                {/* RIGHT COLUMN: Product Details */}
                <div className="space-y-8 text-gray-800">
                    
                    <div>
                        <h1 className="text-4xl font-bold text-[--primary-wine-dark]">
                            {product.name}
                        </h1>

                        {isOutOfStock && (
                            <span className="inline-block mt-3 bg-red-700 text-white text-sm font-bold px-4 py-1.5 rounded-full uppercase tracking-wide">
                                Out of Stock
                            </span>
                        )}

                        {product.tagline && (
                            <p className="text-lg font-semibold text-[--primary-wine] mt-1">
                                {product.tagline}
                            </p>
                        )}
                        
                        {/* ADDED BOTTLE SIZE HERE */}
                        <p className="text-xl text-gray-500 font-medium mt-2">
                            {product.size} Bottle
                        </p>
                    </div>

                    {product.benefitsOverview ? (
                        <>
                            <div>
                                <h2 className="text-xl font-bold text-[--primary-wine] mb-2 border-b pb-2">
                                    {product.benefitsOverview.title}
                                </h2>
                                <p className="leading-relaxed mt-3 text-[15px]">
                                    {product.benefitsOverview.intro}
                                </p>
                            </div>

                            <div>
                                <h2 className="text-lg font-bold text-[--primary-wine] mb-3">Key Health Benefits</h2>
                                <ol className="list-decimal pl-5 space-y-4 text-[15px] leading-relaxed">
                                    {product.benefitsOverview.keyHealthBenefits.map((benefit, i) => (
                                        <li key={i}>
                                            <span className="font-semibold">{benefit.title}</span>
                                            <p className="mt-1">{benefit.description}</p>
                                        </li>
                                    ))}
                                </ol>
                            </div>

                            <div>
                                <h2 className="text-lg font-bold text-[--primary-wine] mb-2">Nutritional Highlights</h2>
                                <ul className="list-disc pl-5 space-y-1 text-[15px] leading-relaxed">
                                    {product.benefitsOverview.nutritionalHighlights.map((n, i) => (
                                        <li key={i}>{n}</li>
                                    ))}
                                </ul>
                            </div>

                            <div>
                                <h2 className="text-lg font-bold text-[--primary-wine] mb-2">Fermentation Characteristics</h2>
                                <p className="leading-relaxed text-[15px] text-gray-700">
                                    {product.benefitsOverview.fermentationCharacteristics}
                                </p>
                            </div>

                            <div className="bg-gray-50 border-l-4 border-[--primary-wine] p-4 rounded-r-lg">
                                <h3 className="font-bold text-sm mb-2">Considerations for Positioning</h3>
                                <p className="text-sm text-gray-600 leading-relaxed">
                                    {product.benefitsOverview.positioning}
                                </p>
                            </div>

                            <div>
                                <h2 className="text-lg font-bold text-[--primary-wine] mb-2">Source Note</h2>
                                <p className="text-sm text-gray-600 leading-relaxed">
                                    {product.benefitsOverview.sourceNote}
                                </p>
                            </div>
                        </>
                    ) : (
                        <>
                            <div>
                                <h2 className="text-xl font-bold text-[--primary-wine] mb-2 border-b pb-2">
                                    Wine Origins & What’s in the Bottle
                                </h2>
                                <p className="leading-relaxed mt-3">
                                    <strong>Ingredients:</strong> {product.ingredients}<br />
                                    <strong>Fermentation:</strong> {product.fermentation}<br />
                                    <strong>Unique Twist:</strong> {product.twist}
                                </p>
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                                <div>
                                    <h2 className="text-lg font-bold text-[--primary-wine] mb-2">Tasting Terms</h2>
                                    <ul className="list-disc pl-5 space-y-1">
                                        {product.tastingTerms.map((term, i) => <li key={i}>{term}</li>)}
                                    </ul>
                                </div>
                                <div>
                                    <h2 className="text-lg font-bold text-[--primary-wine] mb-2">Flavor Descriptions</h2>
                                    <ul className="list-disc pl-5 space-y-1">
                                        {product.flavor.map((f, i) => <li key={i}>{f}</li>)}
                                    </ul>
                                </div>
                            </div>

                            <div>
                                <h2 className="text-lg font-bold text-[--primary-wine] mb-2">Serving Suggestions</h2>
                                <ul className="list-disc pl-5 space-y-1">
                                    {product.serving.map((s, i) => <li key={i}>{s}</li>)}
                                </ul>
                            </div>

                            {product.functionalBenefits && (
                                <div className="bg-pink-50/60 border border-pink-100 rounded-xl p-5">
                                    <h2 className="text-xl font-bold text-[--primary-wine] mb-1">
                                        Functional Benefits Overview
                                    </h2>
                                    <p className="text-sm text-gray-500 mb-3 italic">
                                        Linked to red dragon fruit and its bioactive compounds — not established medical effects of alcoholic wine.
                                    </p>
                                    <ul className="list-disc pl-5 space-y-2 text-[15px] leading-relaxed">
                                        {product.functionalBenefits.map((b, i) => <li key={i}>{b}</li>)}
                                    </ul>
                                </div>
                            )}

                            {product.nutritionalHighlights && (
                                <div>
                                    <h2 className="text-lg font-bold text-[--primary-wine] mb-2">Nutritional Highlights</h2>
                                    <ul className="list-disc pl-5 space-y-1">
                                        {product.nutritionalHighlights.map((n, i) => <li key={i}>{n}</li>)}
                                    </ul>
                                </div>
                            )}

                            {product.fermentationCharacteristics && (
                                <div>
                                    <h2 className="text-lg font-bold text-[--primary-wine] mb-2">Fermentation Characteristics</h2>
                                    <p className="leading-relaxed text-[15px] text-gray-700">{product.fermentationCharacteristics}</p>
                                </div>
                            )}

                            {product.disclaimer && (
                                <div className="bg-gray-50 border-l-4 border-[--primary-wine] p-4 rounded-r-lg">
                                    <h3 className="font-bold text-sm mb-1">Responsible Positioning</h3>
                                    <p className="text-sm text-gray-600 leading-relaxed">{product.disclaimer}</p>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </section>
    );
};

export default ProductDetail;